import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, SlidersHorizontal, Plus, Users, Eye, ChevronRight, Star, Zap, TrendingUp, Timer, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion, AnimatePresence } from 'framer-motion'
import { pageContainer, fadeInUp, cardReveal, hoverLift, tapScale, modalContent } from '@/lib/motion'
import { Pagination, usePagination } from '@/components/shared/Pagination'
import { SkeletonCard } from '@/components/shared/SkeletonLoader'
import { Drawer } from 'vaul'
import { useCollapsibleHeader } from '@/hooks/useCollapsibleHeader'

type Room = {
  id: string
  stakes: string
  buyIn: string
  players: number
  maxPlayers: number
  commission: number
  agentCommission?: number
  status: string
  seats: number
  spectators: number
  host: { name: string; avatar: string } | null
  isHot: boolean
  avgPot: number
  handsPerHour: number
  playerList?: { name: string; stack: string; country: string }[]
}

// Mock room data - expanded for pagination demo
const mockRooms: Room[] = [
  { id: '1', stakes: '1/2', buyIn: '$40-$200', players: 5, maxPlayers: 9, commission: 5, status: 'in_hand', seats: 4, spectators: 12, host: null, isHot: true, avgPot: 4500, handsPerHour: 32, playerList: [
    { name: '王大明', stack: '$185', country: '🇹🇼' },
    { name: 'Mike', stack: '$320', country: '🇺🇸' },
    { name: '小陳', stack: '$95', country: '🇹🇼' },
    { name: 'Alex', stack: '$210', country: '🇯🇵' },
    { name: '阿豪', stack: '$150', country: '🇹🇼' },
  ]},
  { id: '2', stakes: '5/10', buyIn: '$200-$1,000', players: 3, maxPlayers: 6, commission: 5, agentCommission: 1, status: 'waiting', seats: 3, spectators: 4, host: { name: 'VIP俱樂部', avatar: '👑' }, isHot: false, avgPot: 18500, handsPerHour: 28, playerList: [
    { name: '豪哥', stack: '$800', country: '🇹🇼' },
    { name: 'Sarah', stack: '$550', country: '🇺🇸' },
    { name: '老王', stack: '$1,000', country: '🇹🇼' },
  ]},
  { id: '3', stakes: '25/50', buyIn: '$1,000-$5,000', players: 8, maxPlayers: 9, commission: 5, status: 'in_hand', seats: 1, spectators: 28, host: null, isHot: true, avgPot: 95000, handsPerHour: 24 },
  { id: '4', stakes: '1/2', buyIn: '$40-$200', players: 2, maxPlayers: 9, commission: 5, agentCommission: 2, status: 'waiting', seats: 7, spectators: 2, host: { name: '鯊魚池', avatar: '🦈' }, isHot: false, avgPot: 3200, handsPerHour: 35 },
  { id: '5', stakes: '5/10', buyIn: '$200-$1,000', players: 6, maxPlayers: 9, commission: 5, status: 'in_hand', seats: 3, spectators: 15, host: null, isHot: false, avgPot: 22000, handsPerHour: 26 },
  { id: '6', stakes: '1/2', buyIn: '$40-$200', players: 4, maxPlayers: 9, commission: 5, status: 'waiting', seats: 5, spectators: 8, host: null, isHot: false, avgPot: 3800, handsPerHour: 30 },
  { id: '7', stakes: '5/10', buyIn: '$200-$1,000', players: 7, maxPlayers: 9, commission: 4, status: 'in_hand', seats: 2, spectators: 20, host: { name: '高手俱樂部', avatar: '🎯' }, isHot: true, avgPot: 25000, handsPerHour: 22 },
  { id: '8', stakes: '25/50', buyIn: '$1,000-$5,000', players: 5, maxPlayers: 6, commission: 5, status: 'waiting', seats: 1, spectators: 35, host: null, isHot: false, avgPot: 85000, handsPerHour: 20 },
  { id: '9', stakes: '1/2', buyIn: '$40-$200', players: 3, maxPlayers: 9, commission: 6, status: 'in_hand', seats: 6, spectators: 5, host: null, isHot: false, avgPot: 2900, handsPerHour: 38 },
  { id: '10', stakes: '5/10', buyIn: '$200-$1,000', players: 9, maxPlayers: 9, commission: 5, status: 'in_hand', seats: 0, spectators: 42, host: null, isHot: true, avgPot: 30000, handsPerHour: 25 },
  { id: '11', stakes: '50/100', buyIn: '$2,000-$10,000', players: 4, maxPlayers: 6, commission: 4, status: 'in_hand', seats: 2, spectators: 65, host: { name: '豪客廳', avatar: '💎' }, isHot: true, avgPot: 210000, handsPerHour: 18 },
  { id: '12', stakes: '1/2', buyIn: '$40-$200', players: 6, maxPlayers: 9, commission: 5, status: 'waiting', seats: 3, spectators: 3, host: null, isHot: false, avgPot: 4100, handsPerHour: 33 },
]

const stakeFilters = ['全部', '$1/$2', '$5/$10', '$25/$50', '$50/$100']
const playerCountFilters = ['任何', '1-3', '4-6', '7-9']
const roomTypeFilters = ['全部', '公開', '私人']

export function RoomsScreen() {
  const navigate = useNavigate()
  const [selectedStake, setSelectedStake] = useState('全部')
  const [showFilters, setShowFilters] = useState(false)
  const [previewRoom, setPreviewRoom] = useState<Room | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const { headerRef, isMinimized } = useCollapsibleHeader()

  // Filter states
  const [playerCountFilter, setPlayerCountFilter] = useState('任何')
  const [roomTypeFilter, setRoomTypeFilter] = useState('全部')
  const [maxCommission, setMaxCommission] = useState(10)
  const [hideFullRooms, setHideFullRooms] = useState(false)
  const PASS_LENGTH = 6
  const [roomPassword, setRoomPassword] = useState<string[]>(Array(PASS_LENGTH).fill(''))
  const passRefs = useRef<(HTMLInputElement | null)[]>([])
  const isPasswordComplete = roomPassword.every(c => c !== '')

  const handlePassChange = (index: number, value: string) => {
    const char = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(-1)
    const next = [...roomPassword]
    next[index] = char
    setRoomPassword(next)
    if (char && index < PASS_LENGTH - 1) passRefs.current[index + 1]?.focus()
  }

  const handlePassKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !roomPassword[index] && index > 0) passRefs.current[index - 1]?.focus()
  }

  const handlePassPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, PASS_LENGTH)
    const next = [...roomPassword]
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i]
    setRoomPassword(next)
    passRefs.current[Math.min(pasted.length, PASS_LENGTH - 1)]?.focus()
  }

  const activeFilterCount = [
    playerCountFilter !== '任何',
    roomTypeFilter !== '全部',
    maxCommission < 10,
    hideFullRooms,
  ].filter(Boolean).length

  const filteredRooms = mockRooms.filter(r => {
    const matchesStake = selectedStake === '全部' || r.stakes === selectedStake.replaceAll('$', '')
    if (!matchesStake) return false

    // Player count filter
    if (playerCountFilter !== '任何') {
      const [min, max] = playerCountFilter.split('-').map(Number)
      if (r.players < min || r.players > max) return false
    }

    // Room type filter
    if (roomTypeFilter === '公開' && r.host) return false
    if (roomTypeFilter === '私人' && !r.host) return false

    // Max commission
    const totalCommission = r.commission + (r.agentCommission || 0)
    if (totalCommission > maxCommission) return false

    // Hide full rooms
    if (hideFullRooms && r.seats === 0) return false

    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    return (
      r.id.includes(q) ||
      r.stakes.includes(q) ||
      r.buyIn.toLowerCase().includes(q) ||
      (r.host?.name.toLowerCase().includes(q) ?? false)
    )
  })

  const {
    currentPage,
    pageSize,
    totalPages,
    totalItems,
    paginatedItems: paginatedRooms,
    setCurrentPage,
    setPageSize,
  } = usePagination(filteredRooms, 5)

  return (
    <motion.div
      variants={pageContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col min-h-full bg-background relative overflow-x-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] gradient-radial-gold pointer-events-none" />
      <div className="fixed inset-0 noise-overlay pointer-events-none" />

      {/* Expanded Header — normal flow, scrolls away */}
      <motion.header ref={headerRef} variants={fadeInUp} className="relative z-10">
        <div className="px-4 pt-4 flex items-center justify-between mb-4">
          <h1 className="font-display text-3xl font-semibold text-foreground">大廳</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsLoading(!isLoading)}
              className={cn("text-[10px] font-mono px-2 py-1 rounded-lg transition-all border border-dashed", isLoading ? "bg-rose-500/10 text-rose-400 border-rose-500/30" : "bg-transparent text-muted-foreground/40 border-foreground/10")}
            >
              [DEV] 載入中
            </button>
            <motion.button
              whileTap={tapScale}
              onClick={() => navigate('/create-room')}
              className="flex items-center gap-2 px-4 py-2 btn-primary rounded-full text-sm font-bold"
            >
              <Plus className="w-4 h-4" />
              創建
            </motion.button>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 flex gap-3 mb-4">
          <div className="flex-1 relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-gold transition-colors duration-200" />
            <input
              type="text"
              placeholder="搜尋房間或房主 ID..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1) }}
              className="w-full h-12 pl-10 pr-4 glass-card rounded-xl text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-gold/50 transition-colors duration-200"
            />
          </div>
          <motion.button
            whileTap={tapScale}
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'h-12 w-12 flex items-center justify-center rounded-xl border transition-colors duration-200 relative',
              showFilters
                ? 'bg-gold text-background border-gold shadow-glow-gold'
                : 'glass-card text-muted-foreground hover:text-gold hover:border-gold/30'
            )}
          >
            <SlidersHorizontal className="w-5 h-5" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gold text-background text-[10px] font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </motion.button>
        </div>

        {/* Filter Chips */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide px-4 pb-3">
          {stakeFilters.map((stake) => (
            <motion.button
              key={stake}
              whileTap={tapScale}
              onClick={() => {
                setSelectedStake(stake)
                setCurrentPage(1)
              }}
              className={cn(
                'flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold tracking-wide transition-colors duration-200 border',
                selectedStake === stake
                  ? 'bg-gold text-background border-gold shadow-glow-gold'
                  : 'glass-card text-muted-foreground border-foreground/5 hover:text-foreground hover:border-gold/30'
              )}
            >
              {stake}
            </motion.button>
          ))}
        </div>
      </motion.header>

      {/* Minimized sticky bar — overlays content, no layout space */}
      <div className="sticky top-0 z-40 h-0">
        <AnimatePresence>
          {isMinimized && (
            <motion.div
              initial={{ y: -48, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -48, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
              className="px-4 py-2 flex items-center gap-2 bg-background/95 backdrop-blur-xl border-b border-foreground/5"
            >
              <div className="flex-1 relative group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground group-focus-within:text-gold transition-colors duration-200" />
                <input
                  type="text"
                  placeholder="搜尋..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1) }}
                  className="w-full h-10 pl-9 pr-3 glass-card rounded-lg text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-gold/50 transition-colors duration-200"
                />
              </div>
              <motion.button
                whileTap={tapScale}
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  'h-10 w-10 flex items-center justify-center rounded-lg border transition-colors duration-200 relative',
                  showFilters
                    ? 'bg-gold text-background border-gold'
                    : 'glass-card text-muted-foreground hover:text-gold hover:border-gold/30'
                )}
              >
                <SlidersHorizontal className="w-4 h-4" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gold text-background text-[9px] font-bold flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </motion.button>
              <motion.button
                whileTap={tapScale}
                onClick={() => navigate('/create-room')}
                className="h-10 w-10 flex items-center justify-center rounded-lg btn-primary"
              >
                <Plus className="w-4 h-4" />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Filter Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            variants={modalContent}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="px-4"
          >
            <div className="p-5 glass-card rounded-2xl space-y-5 mb-4">
              {/* Room Status */}
              <div className="space-y-3">
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">房間狀態</label>
                <div className="flex flex-wrap gap-2">
                  {['有空位', '排隊中', '進行中'].map(status => (
                    <motion.button
                      key={status}
                      whileTap={tapScale}
                      className="px-3 py-1.5 rounded-lg bg-surface-elevated border border-foreground/5 text-xs text-muted-foreground hover:border-gold/30 hover:text-gold transition-colors duration-200"
                    >
                      {status}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Player Count */}
              <div className="space-y-3">
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">玩家人數</label>
                <div className="flex gap-2">
                  {playerCountFilters.map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setPlayerCountFilter(filter)}
                      className={cn(
                        'flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all border',
                        playerCountFilter === filter
                          ? 'bg-gold/15 text-gold border-gold/30'
                          : 'bg-surface-elevated border-foreground/5 text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>

              {/* Room Type */}
              <div className="space-y-3">
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">房間類型</label>
                <div className="flex gap-2">
                  {roomTypeFilters.map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setRoomTypeFilter(filter)}
                      className={cn(
                        'flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all border',
                        roomTypeFilter === filter
                          ? 'bg-gold/15 text-gold border-gold/30'
                          : 'bg-surface-elevated border-foreground/5 text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>

              {/* Max Commission */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">最高抽水</label>
                  <span className="text-xs font-mono font-bold text-gold">{maxCommission}%</span>
                </div>
                <div className="relative pt-1 pb-5">
                  <input
                    type="range"
                    min={1}
                    max={10}
                    value={maxCommission}
                    onChange={(e) => setMaxCommission(Number(e.target.value))}
                    className="w-full h-1.5 bg-surface-elevated rounded-full appearance-none accent-gold relative z-10"
                  />
                  <div className="absolute left-0 right-0 top-[11px] flex justify-between px-[2px] pointer-events-none">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(v => (
                      <div key={v} className="flex flex-col items-center gap-1">
                        <div className={cn(
                          "w-1 h-1 rounded-full transition-colors",
                          v <= maxCommission ? "bg-gold" : "bg-foreground/20"
                        )} />
                        {(v === 1 || v === 5 || v === 10) && (
                          <span className={cn(
                            "text-[8px] font-mono transition-colors",
                            v === maxCommission ? "text-gold font-bold" : "text-foreground/20"
                          )}>{v}%</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Hide Full Rooms Toggle */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated border border-foreground/5">
                <span className="text-xs font-semibold text-foreground">隱藏滿座房間</span>
                <button
                  onClick={() => setHideFullRooms(!hideFullRooms)}
                  className={cn(
                    "w-10 h-6 rounded-full transition-colors duration-200 relative",
                    hideFullRooms ? "bg-gold" : "bg-foreground/10"
                  )}
                >
                  <div className={cn(
                    "absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-200",
                    hideFullRooms ? "translate-x-[18px]" : "translate-x-0.5"
                  )} />
                </button>
              </div>

              <div className="flex gap-3 pt-2">
                <motion.button
                  whileTap={tapScale}
                  onClick={() => {
                    setPlayerCountFilter('任何')
                    setRoomTypeFilter('全部')
                    setMaxCommission(10)
                    setHideFullRooms(false)
                    setShowFilters(false)
                  }}
                  className="flex-1 py-3 rounded-xl glass-card text-muted-foreground text-sm font-semibold hover:bg-foreground/5 transition-colors duration-200"
                >
                  重置
                </motion.button>
                <motion.button
                  whileTap={tapScale}
                  onClick={() => { setShowFilters(false); setCurrentPage(1) }}
                  className="flex-1 py-3 rounded-xl btn-primary text-sm font-semibold"
                >
                  套用
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Room List */}
      <motion.main
        variants={pageContainer}
        className="px-4 pb-4 space-y-3 relative z-10"
      >
        {isLoading ? (
          <SkeletonCard count={4} />
        ) : (
        <>
        {paginatedRooms.map((room) => (
          <motion.div
            key={room.id}
            variants={cardReveal}
            whileHover={hoverLift}
            whileTap={tapScale}
            onClick={() => setPreviewRoom(room)}
            className={cn(
              "group w-full p-4 glass-card rounded-2xl transition-colors duration-200 cursor-pointer relative overflow-hidden",
              room.isHot && "hot-glow border-gold/30"
            )}
          >
            {room.isHot && (
              <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-gold/20 border border-gold/30">
                <span className="text-[10px] font-bold text-gold uppercase tracking-wider">🔥 熱門</span>
              </div>
            )}

            {room.status === 'in_hand' && (
              <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 blur-2xl rounded-full -translate-y-1/2 translate-x-1/2" />
            )}

            <div className="flex justify-between items-start mb-4 relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-surface-elevated border border-foreground/5 flex flex-col items-center justify-center group-hover:border-gold/20 transition-colors duration-200">
                  <span className="text-xs text-muted-foreground font-medium">盲注</span>
                  <span className="text-lg font-bold text-gradient-gold tracking-tight">${room.stakes}</span>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {room.host && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gold/10 text-gold border border-gold/20 font-bold uppercase tracking-wider">
                        私人
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground font-mono tracking-wide">#{room.id.padStart(4, '0')}</span>
                  </div>
                  <div className="text-sm text-muted-foreground font-medium">買入: <span className="text-foreground">{room.buyIn}</span></div>
                </div>
              </div>

              {!room.isHot && (
                <div className={cn(
                  'px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border',
                  room.status === 'in_hand'
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                    : 'bg-surface-elevated text-muted-foreground border-foreground/5'
                )}>
                  {room.status === 'in_hand' ? '進行中' : '等待中'}
                </div>
              )}
            </div>

            {/* Info Grid — enhanced with avgPot and handsPerHour */}
            <div className="grid grid-cols-5 gap-2 relative z-10">
              <div className="p-2 rounded-xl bg-surface-sunken flex flex-col items-center justify-center border border-foreground/5">
                <Users className="w-4 h-4 text-muted-foreground mb-1" />
                <span className="text-xs font-bold text-foreground">{room.players}/{room.maxPlayers}</span>
              </div>
              <div className="p-2 rounded-xl bg-surface-sunken flex flex-col items-center justify-center border border-foreground/5">
                <span className="text-xs font-bold text-gold mb-1">{room.commission + (room.agentCommission || 0)}%</span>
                <span className="text-[10px] text-muted-foreground uppercase">抽水</span>
              </div>
              <div className="p-2 rounded-xl bg-surface-sunken flex flex-col items-center justify-center border border-foreground/5">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-400 mb-1" />
                <span className="text-[10px] font-bold text-foreground">${(room.avgPot / 100).toFixed(0)}</span>
              </div>
              <div className="p-2 rounded-xl bg-surface-sunken flex flex-col items-center justify-center border border-foreground/5">
                <Timer className="w-3.5 h-3.5 text-muted-foreground mb-1" />
                <span className="text-[10px] font-bold text-foreground">{room.handsPerHour}/h</span>
              </div>
              {room.host ? (
                <div className="p-2 rounded-xl bg-gold/5 flex flex-col items-center justify-center border border-gold/10">
                  <Lock className="w-3.5 h-3.5 text-gold mb-0.5" />
                  <span className="text-[10px] text-gold truncate max-w-full">私人</span>
                </div>
              ) : (
                <div className="p-2 rounded-xl bg-surface-sunken flex flex-col items-center justify-center border border-foreground/5">
                  <Eye className="w-3.5 h-3.5 text-muted-foreground mb-1" />
                  <span className="text-[10px] font-bold text-foreground">{room.spectators}</span>
                </div>
              )}
            </div>
          </motion.div>
        ))}

        {/* Empty State */}
        {paginatedRooms.length === 0 && (
          <motion.div variants={fadeInUp} className="flex flex-col items-center py-20">
            <div className="w-16 h-16 rounded-full bg-surface-elevated flex items-center justify-center mb-4">
              <Search className="w-8 h-8 text-muted-foreground/40" />
            </div>
            <p className="text-lg font-semibold text-foreground mb-2">沒有符合條件的房間</p>
            <p className="text-sm text-muted-foreground mb-6">嘗試調整篩選條件</p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setSelectedStake('全部')
                  setPlayerCountFilter('任何')
                  setRoomTypeFilter('全部')
                  setMaxCommission(10)
                  setHideFullRooms(false)
                  setCurrentPage(1)
                }}
                className="px-6 py-3 glass-card rounded-xl font-bold text-sm text-muted-foreground hover:text-foreground hover:border-gold/30 transition-colors duration-200"
              >
                清除篩選
              </button>
              <button
                onClick={() => navigate('/create-room')}
                className="px-6 py-3 btn-primary rounded-xl font-bold text-sm"
              >
                創建房間
              </button>
            </div>
          </motion.div>
        )}

        {/* Pagination */}
        {paginatedRooms.length > 0 && (
          <div className="pt-4 pb-20">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[5, 10, 20]}
            />
          </div>
        )}
        </>
        )}
      </motion.main>

      {/* ==================== ROOM PREVIEW DRAWER ==================== */}
      <Drawer.Root open={previewRoom !== null} onOpenChange={(open) => { if (!open) { setPreviewRoom(null); setRoomPassword(Array(PASS_LENGTH).fill('')) } }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 max-h-[85vh] overflow-y-auto">
              {/* Handle */}
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-foreground/20" />
              </div>

              {previewRoom && (
                <div className="px-6 pb-8 safe-area-bottom">
                  {/* Room Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <Drawer.Title className="font-display text-2xl font-semibold text-foreground flex items-center gap-2">
                        德州撲克 #{previewRoom.id.padStart(4, '0')}
                        {previewRoom.isHot && <Zap className="w-5 h-5 text-gold" />}
                      </Drawer.Title>
                      {previewRoom.host && (
                        <p className="text-sm text-gold mt-1 flex items-center gap-1.5">
                          <span>{previewRoom.host.avatar}</span>
                          <span className="font-semibold">{previewRoom.host.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gold/10 border border-gold/20 font-bold">私人房</span>
                        </p>
                      )}
                    </div>
                    <div className={cn(
                      'px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider border',
                      previewRoom.status === 'in_hand'
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        : 'bg-surface-elevated text-muted-foreground border-foreground/5'
                    )}>
                      {previewRoom.status === 'in_hand' ? '進行中' : '等待中'}
                    </div>
                  </div>

                  {/* Room Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="p-4 rounded-2xl bg-surface-elevated border border-foreground/5">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">盲注</p>
                      <p className="text-2xl font-bold text-gradient-gold">${previewRoom.stakes}</p>
                    </div>
                    <div className="p-4 rounded-2xl bg-surface-elevated border border-foreground/5">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">買入範圍</p>
                      <p className="text-lg font-bold text-foreground">{previewRoom.buyIn}</p>
                    </div>
                  </div>

                  {/* Commission Breakdown */}
                  <div className="p-4 rounded-2xl glass-card mb-6">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3 font-bold">佣金明細</p>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">平台抽水</span>
                        <span className="text-sm font-bold font-mono text-foreground">{previewRoom.commission}%</span>
                      </div>
                      {previewRoom.agentCommission && previewRoom.agentCommission > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">房主佣金</span>
                          <span className="text-sm font-bold font-mono text-gold">{previewRoom.agentCommission}%</span>
                        </div>
                      )}
                      <div className="border-t border-foreground/5 pt-2 flex justify-between">
                        <span className="text-sm font-semibold text-foreground">總計</span>
                        <span className="text-sm font-bold font-mono text-gold">{previewRoom.commission + (previewRoom.agentCommission || 0)}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Players at Table */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide font-bold flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        在座玩家 ({previewRoom.players}/{previewRoom.maxPlayers})
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Eye className="w-3.5 h-3.5" />
                        {previewRoom.spectators} 觀戰
                      </p>
                    </div>
                    <div className="space-y-2">
                      {(previewRoom.playerList || []).map((player, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated">
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{player.country}</span>
                            <span className="text-sm font-medium text-foreground">{player.name}</span>
                          </div>
                          <span className="text-sm font-mono font-bold text-muted-foreground">{player.stack}</span>
                        </div>
                      ))}
                      {previewRoom.seats > 0 && (
                        <div className="flex items-center gap-2 p-3 rounded-xl border border-dashed border-gold/20 bg-gold/5">
                          <Star className="w-4 h-4 text-gold" />
                          <span className="text-sm text-gold font-medium">{previewRoom.seats} 個空位</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Passcode Field (Private Rooms) */}
                  {previewRoom.host && (
                    <div className="mb-4">
                      <p className="text-xs text-muted-foreground text-center mb-3 font-medium flex items-center justify-center gap-1.5">
                        <Lock className="w-3.5 h-3.5" />
                        輸入房間密碼
                      </p>
                      <div className="flex items-center justify-center gap-2">
                        {Array.from({ length: PASS_LENGTH }).map((_, index) => (
                          <input
                            key={index}
                            ref={(el) => { passRefs.current[index] = el }}
                            type="text"
                            inputMode="text"
                            maxLength={1}
                            value={roomPassword[index]}
                            onChange={(e) => handlePassChange(index, e.target.value)}
                            onKeyDown={(e) => handlePassKeyDown(index, e)}
                            onPaste={index === 0 ? handlePassPaste : undefined}
                            className={cn(
                              "w-11 h-14 rounded-xl bg-surface-elevated border-2 text-center text-xl font-mono font-bold text-foreground",
                              "focus:outline-none focus:border-gold focus:bg-surface-elevated transition-all duration-200",
                              "placeholder:text-muted-foreground/20",
                              roomPassword[index] ? "border-foreground/20" : "border-foreground/10"
                            )}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Button */}
                  <button
                    onClick={() => {
                      setPreviewRoom(null)
                      setRoomPassword(Array(PASS_LENGTH).fill(''))
                      navigate(`/room/${previewRoom.id}`)
                    }}
                    disabled={!!previewRoom.host && !isPasswordComplete}
                    className={cn(
                      "w-full h-14 rounded-2xl font-bold text-lg flex items-center justify-center gap-2",
                      !!previewRoom.host && !isPasswordComplete
                        ? "bg-surface-elevated text-muted-foreground/50 cursor-not-allowed border border-foreground/5"
                        : "btn-primary"
                    )}
                  >
                    <ChevronRight className="w-5 h-5" />
                    加入房間
                  </button>
                </div>
              )}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
